import styled, { css, keyframes } from 'styled-components';
import { AiSparkle16 } from 'ui/icons/aurora/16px/AiSparkle16';
import { easing, spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';

const SPARKLE_SIZE = 16;
const COUNT = 3;
const DURATION = '1500ms';
const DELAY = '100ms';
const SCALE = 2.6;
const DEFAULT_WIDTH = 108;

export type GenAiLoadingAnimationProps = {
  id?: string;
  testId?: string;
  /** Width of loader */
  width?: number;
  /** Number of rotations  */
  iterationCount?: number;
};

type SparkleProps = {
  $iterationCount?: number;
  $gap: number;
  $idx: number;
  width: number;
};

const createAnimation = ({ $gap, width = SPARKLE_SIZE, $idx }: SparkleProps) => {
  const x = width + $gap;
  const fromCenter = $idx === Math.floor(COUNT / 2);
  const toCenter = $idx === Math.ceil(COUNT / 2);

  let translateX = -x; // rotate to the left
  if ($idx === 0) translateX = x * (COUNT - 1); // rotate to the end
  if (toCenter) translateX = -(x / SCALE); // rotate to the center

  return keyframes`
      from {
        opacity: ${fromCenter ? 1 : 0.5};
        transform: scale(${fromCenter ? SCALE : 1});
      }
      to {
        opacity: ${toCenter ? 1 : 0.5};
         transform: scale(${toCenter ? SCALE : 1}) translateX(${translateX}px) rotate(${
           toCenter ? '' : '-'
         }90deg);
      }
  `;
};

const LoadingIcons = styled.div<{ gap: number }>`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${({ gap }) => pxToRem(gap)};
  margin-bottom: ${spacing.plusHalf};
`;

const Sparkle = styled(AiSparkle16)<SparkleProps>`
  ${(props) =>
    css`
      animation: ${createAnimation(props)} ${DURATION} ${easing.standard} ${DELAY}
        ${props.$iterationCount || 'infinite'} forwards;
    `};
`;

export const GenAiLoadingAnimation = ({
  id,
  testId,
  width = DEFAULT_WIDTH,
  iterationCount,
  ...props
}: GenAiLoadingAnimationProps) => {
  const scale = width / DEFAULT_WIDTH;
  const sparkleSize = scale * SPARKLE_SIZE;
  const gap = (width - sparkleSize * COUNT) / (COUNT - 1);

  return (
    <LoadingIcons data-test={testId} id={id} gap={gap} {...props}>
      {Array.from({ length: COUNT }).map((_, index) => (
        <Sparkle
          $iterationCount={iterationCount}
          $gap={gap}
          $idx={index}
          height={sparkleSize}
          width={sparkleSize}
          key={`sparkle-${index}`}
        />
      ))}
    </LoadingIcons>
  );
};
